import React, { useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Shield, Download, Trash2, MailX, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GDPRPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [exportData, setExportData] = useState(null);

  const handleExport = async () => {
    if (!email.trim()) { setError("Vennligst skriv inn e-postadressen din"); return; }
    setLoading(true); setError(""); setResult(null); setExportData(null);
    try {
      const res = await axios.post(`${API}/gdpr/export-data`, { email: email.trim() });
      setExportData(res.data);
      setResult({ type: "export", message: "Dine data er hentet. Du kan laste ned nedenfor." });
    } catch (err) {
      setError(err.response?.data?.detail || "Noe gikk galt. Prov igjen.");
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!email.trim()) { setError("Vennligst skriv inn e-postadressen din"); return; }
    setResult({ type: "delete", message: "For a slette dine data, vennligst kontakt oss pa telefon +47 48 91 51 90 eller send e-post til oss. Vi behandler foresporselen din innen 30 dager i henhold til GDPR." });
  };

  const handleUnsubscribe = async () => {
    if (!email.trim()) { setError("Vennligst skriv inn e-postadressen din"); return; }
    setLoading(true); setError(""); setResult(null); setExportData(null);
    try {
      const res = await axios.post(`${API}/gdpr/unsubscribe`, { email: email.trim() });
      setResult({ type: "unsubscribe", message: res.data.message });
    } catch (err) {
      setError(err.response?.data?.detail || "Noe gikk galt. Prov igjen.");
    }
    setLoading(false);
  };

  const downloadData = () => {
    if (!exportData) return;
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mine-data-${email.replace("@", "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      <section className="py-16" style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Shield className="h-16 w-16 mx-auto mb-4" style={{ color: '#d4af37' }} />
            <h1 className="text-4xl sm:text-5xl font-serif-display font-bold mb-4" style={{ color: '#d4af37' }} data-testid="gdpr-title">
              Dine Rettigheter
            </h1>
            <p className="text-lg" style={{ color: '#f5e6d3' }}>
              I henhold til GDPR har du rett til innsyn, sletting og dataportabilitet
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-8 sm:p-10"
            style={{ border: '2px solid #d4af37' }}
          >
            <div className="mb-8">
              <label className="block text-sm font-semibold mb-2" style={{ color: '#2d1810' }}>
                Din e-postadresse
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                placeholder="din@epost.no"
                className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-colors"
                style={{ borderColor: '#d4af37', color: '#2d1810' }}
                data-testid="gdpr-email-input"
              />
            </div>

            <div className="space-y-4">
              <button
                onClick={handleExport}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all hover:scale-[1.02]"
                style={{ background: '#f4e7d8', color: '#2d1810', border: '2px solid #d4af37' }}
                data-testid="gdpr-export-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" style={{ color: '#721111' }} />}
                Last ned mine data (Dataportabilitet)
              </button>

              <button
                onClick={handleUnsubscribe}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all hover:scale-[1.02]"
                style={{ background: '#fef3c7', color: '#92400e', border: '2px solid #f59e0b' }}
                data-testid="gdpr-unsubscribe-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <MailX className="h-5 w-5" />}
                Meld meg av markedsforing
              </button>

              <button
                onClick={handleDelete}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all hover:scale-[1.02]"
                style={{ background: '#fef2f2', color: '#dc2626', border: '2px solid #dc2626' }}
                data-testid="gdpr-delete-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
                Slett alle mine data (Retten til a bli glemt)
              </button>
            </div>

            {error && (
              <div className="mt-6 p-4 rounded-lg flex items-center gap-3" style={{ background: '#fef2f2', border: '1px solid #dc2626' }} data-testid="gdpr-error">
                <AlertCircle className="h-5 w-5 flex-shrink-0" style={{ color: '#dc2626' }} />
                <p style={{ color: '#dc2626' }}>{error}</p>
              </div>
            )}

            {result && (
              <div className="mt-6 p-4 rounded-lg flex items-start gap-3" style={{ background: '#dcfce7', border: '1px solid #22c55e' }} data-testid="gdpr-result">
                <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }} />
                <div>
                  <p className="font-semibold" style={{ color: '#166534' }}>{result.message}</p>
                  {result.data && (
                    <p className="text-sm mt-1" style={{ color: '#166534' }}>
                      Slettet: {result.data.orders} bestillinger, {result.data.bookings} reservasjoner, {result.data.email_subscriptions} e-postabonnement
                    </p>
                  )}
                </div>
              </div>
            )}

            {exportData && (
              <div className="mt-4">
                <button
                  onClick={downloadData}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, #721111, #2d1810)', color: '#d4af37', border: '2px solid #d4af37' }}
                  data-testid="gdpr-download-json"
                >
                  <Download className="h-5 w-5" />
                  Last ned som JSON-fil
                </button>
                <div className="mt-4 p-4 rounded-lg text-sm max-h-60 overflow-auto" style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                  <p className="font-semibold mb-2" style={{ color: '#2d1810' }}>Oversikt over dine data:</p>
                  <p style={{ color: '#4a3728' }}>Bestillinger: {exportData.data.orders.length}</p>
                  <p style={{ color: '#4a3728' }}>Reservasjoner: {exportData.data.bookings.length}</p>
                  <p style={{ color: '#4a3728' }}>E-postabonnement: {exportData.data.email_subscription ? "Ja" : "Nei"}</p>
                  <p style={{ color: '#4a3728' }}>Gavekort kjopt: {exportData.data.gift_cards_purchased.length}</p>
                </div>
              </div>
            )}

            <div className="mt-8 p-4 rounded-lg" style={{ background: '#f4e7d8' }}>
              <p className="text-sm" style={{ color: '#8b4513' }}>
                Merk: Bestillinger og reservasjoner slettes automatisk dagen etter. Gavekortdata beholdes for a sikre gyldigheten av aktive gavekort.
                For sporsmal, kontakt oss pa <a href="mailto:post@masalaindisk.no" className="underline font-semibold">post@masalaindisk.no</a>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GDPRPage;
